import { Autocomplete, AutocompleteItem, AutocompleteProps } from '@heroui/react'
import { Field, FieldProps } from 'formik'
import { motion } from 'framer-motion'

interface Option {
  key: string
  label: string
  startContent?: React.ReactNode
}

type CustomAutocompleteProps = Omit<
  AutocompleteProps<Option>,
  'children' | 'defaultItems' | 'onSelectionChange' | 'onChange'
>

export interface SelectFormikProps extends CustomAutocompleteProps {
  label: string
  options: Option[]
  name: string
  onChange?: (value: string) => void
  placeholder?: string
  customRenderValue?: (value: Option | undefined) => React.ReactNode
}

const SelectAutoCompleteFormik = ({
  label,
  options,
  name,
  onChange,
  placeholder = '',
  customRenderValue,
  ...props
}: SelectFormikProps) => {
  return (
    <motion.div>
      <Field name={name}>
        {({ field, form, meta }: FieldProps) => (
          <Autocomplete<Option>
            {...props}
            {...field}
            defaultItems={options}
            errorMessage={meta.error && meta.touched ? meta.error : undefined}
            isClearable={false}
            label={label}
            labelPlacement="outside"
            placeholder={placeholder}
            radius="sm"
            selectedKey={field.value}
            variant="bordered"
            classNames={{
              popoverContent: 'bg-[#343537] border border-[#707070]',
            }}
            inputProps={{
              classNames: {
                label: [
                  'text-foreground',
                  'dark:text-foreground',
                  'group-data-[filled-within=true]:text-foreground',
                  'dark:group-data-[filled-within=true]:text-white',
                  'group-data-[invalid=true]:text-danger',
                  'dark:group-data-[invalid=true]:text-danger',
                ],
                input: [
                  'bg-transparent',
                  'text-foreground',
                  'dark:text-white',
                  'placeholder:text-default-400',
                  'dark:placeholder:text-white/60',
                  'group-data-[invalid=true]:placeholder:text-danger',
                ],
                innerWrapper: 'bg-transparent',
                inputWrapper: [
                  'bg-default-50',
                  'dark:bg-[#1e1e20]',
                  'border-default-200',
                  'dark:border-default-600',
                  'hover:bg-default-100',
                  'dark:hover:bg-[#1e1e20]/70',
                  'group-data-[focus=true]:bg-default-100',
                  'dark:group-data-[focus=true]:bg-[#1e1e20]/50',
                  'group-data-[focus=true]:border-primary',
                  'dark:group-data-[focus=true]:border-primary',
                  '!cursor-text',
                ],
              },
            }}
            onClear={() => {
              form.setFieldValue(name, '')
              onChange?.('')
            }}
            onSelectionChange={key => {
              form.setFieldValue(name, key?.toString() || '')
              onChange?.(key?.toString() || '')
            }}
          >
            {(item: Option) => (
              <AutocompleteItem key={item.key} textValue={item.label}>
                {customRenderValue ? customRenderValue(item) : item.label}
              </AutocompleteItem>
            )}
          </Autocomplete>
        )}
      </Field>
    </motion.div>
  )
}

export default SelectAutoCompleteFormik
